import React, { useState, useEffect, useCallback } from 'react';
import SettingsPanelBody from '../../util/SettingsPanelBody';
import SettingsBlock from '../../util/SettingsBlock';
import axios from 'axios';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import Main from '../../settingsComponents/Main';
import Other from '../../settingsComponents/Other';
import Mail from '../../settingsComponents/Mail';
import MailQueuePanel from '../../settingsComponents/MailQueuePanel';
import TabMenu from '../../util/TabMenu';
import EquipmentSettings from '../../modal/EquipmentSettings';
import TimesheetsSettings, { ToilRecountPanel } from '../../modal/TimesheetsSettings';
import ExpensesSettings from '../../modal/ExpensesSettings';
import CrmSettings from '../../modal/CrmSettings';
import RoleMasterDetail from '../../settingsComponents/RoleMasterDetail';
import UsersManagement from '../../settingsComponents/UsersManagement';
import HrSettingsDrawer from '../../hrComponents/HrSettingsDrawer';
import { t } from 'i18next';
import Payment from '../../settingsComponents/Payment';
import Logs from '../../settingsComponents/Logs';
import RecalcLog from '../../settingsComponents/RecalcLog';
import DiskPage from '../../diskComponents/DiskPage';
import Notifications from '../../settingsComponents/Notifications';
import FieldsSettings from '../../settingsComponents/FieldsSettings';

interface SettingsTabProps {
  access: Record<string, any>;
  baseUrl: string;
  refreshUserHeder: () => void;
  is24HourFormat: boolean;
  handleTimeFormatChange: () => void;
  taskOrTasks: boolean;
  handleTaskOrTasks: () => void;
  timeZone: string | null;
  setTimeZone: (tz: string) => void;
  staticDateSelect: any;
  setStaticDateSelect: (v: any) => void;
  unit: any;
  setUnit: (v: any) => void;
  defaultSection?: string;
  hideSidebar?: boolean;
}

const SettingsTab: React.FC<SettingsTabProps> = ({
  access,
  baseUrl,
  refreshUserHeder,
  is24HourFormat,
  handleTimeFormatChange,
  taskOrTasks,
  handleTaskOrTasks,
  timeZone,
  setTimeZone,
  staticDateSelect,
  setStaticDateSelect,
  unit,
  setUnit,
  defaultSection,
  hideSidebar = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  // Active section — local state
  const [activeSection, setActiveSection] = useState<string>('');
  const [logsTab, setLogsTab] = useState('all');

  // Company state
  const [companyID, setCompanyID] = useState<number | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [urlImage, setUrlImage] = useState('');
  const [file, setFile] = useState<File | null>(null);

  // Static data
  const [staticCountry, setStaticCountry] = useState<any[]>([]);
  const [staticCountrySelect, setStaticCountrySelect] = useState('');
  const [staticCurrency, setStaticCurrency] = useState<any[]>([]);
  const [staticCurrencySelect, setStaticCurrencySelect] = useState('');
  const [staticLanguage, setStaticLanguage] = useState<any[]>([]);
  const [staticLanguageSelect, setStaticLanguageSelect] = useState('');
  const [staticTimeZone, setStaticTimeZone] = useState<any[]>([]);
  const [staticTimeZoneSelected, setStaticTimeZoneSelected] = useState('');
  const [staticDate, setStaticDate] = useState<any[]>([]);
  const [staticNumber, setStaticNumber] = useState<any[]>([]);
  const [staticNumberSelect, setStaticNumberSelect] = useState('');
  const [userTimeZone, setUserTimeZone] = useState<string | null>(null);
  const [utcOffset, setUtcOffset] = useState<string | null>(null);

  // Mail state
  const [mailHost, setMailHost] = useState('');
  const [mailPort, setMailPort] = useState('');
  const [mailUser, setMailUser] = useState('');
  const [mailService, setMailService] = useState('');
  const [mailPassword, setMailPassword] = useState('*****');
  const [mailCronWeek, setMailCronWeek] = useState(false);
  const [mailCronWeekApprove, setMailCronWeekApprove] = useState(false);
  const [mailAuth, setMailAuth] = useState('');
  const [mailFrom, setMailFrom] = useState('');
  const [mailName, setMailName] = useState('');
  const [financeMail, setFinanceMail] = useState('');
  const [mailSll, setMailSll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMail, setIsLoadingMail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [dayWeek, setDayWeek] = useState('1');
  const [dayWeekApprove, setDayWeekApprove] = useState('1');
  const [timeFrom, setTimeFrom] = useState<Date | null>(moment().hour(9).minute(0).toDate());
  const [timeFromApprove, setTimeFromApprove] = useState<Date | null>(moment().hour(9).minute(0).toDate());
  const [timeTo, setTimeTo] = useState('9');
  const [timeToApprove, setTimeToApprove] = useState('9');

  const [emailTab, setEmailTab] = useState('general');
  const [history, setHistory] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  const hrAccess = access?.users_edit || access?.hr_admin;
  const sections = [
    { key: 'company',      label: 'Company',          icon: '🏢', group: 'general',  access: access?.settings_view },
    { key: 'other',        label: t('Localization') || 'Localization', icon: '🌍', group: 'general',  access: access?.settings_view },
    { key: 'email',         label: 'Email',                           icon: '📧', group: 'general',  access: access?.settings_view },
    { key: 'notifications', label: t('Notification') || 'Notifications', icon: '🔔', group: 'general',  access: access?.settings_view },
    { key: 'payment',       label: t('Billing') || 'Billing',            icon: '💳', group: 'general',  access: access?.settings_view },
    { key: 'fields',        label: t('CustomFields') || 'Fields',        icon: '📝', group: 'general',  access: access?.settings_view },
    { key: 'disk',         label: t('Disk') || 'Disk', icon: '💾', group: 'tools',    access: access?.disk_enabled === true || access?.disk_enabled === 1 || access?.disk_view === true || access?.disk_view === 1 },
    { key: 'logs',         label: t('Logs') || 'Logs', icon: '📋', group: 'tools',    access: access?.history_view === true || access?.history_view === 1 || access?.history_view_all === true || access?.history_view_all === 1 },
    { key: 'timesheets',   label: 'Timesheets',   icon: '🕐', group: 'sections', access: access?.timesheet_settings },
    { key: 'hr',          label: 'HR',           icon: '👥', group: 'sections', access: hrAccess },
    { key: 'equipment',   label: 'Equipment',    icon: '🖥️', group: 'sections', access: access?.equipment_settings },
    { key: 'finance',      label: 'Finance',            icon: '💰', group: 'sections', access: access?.expenses_settings },
    { key: 'crm',          label: 'CRM',                icon: '🤝', group: 'sections', access: access?.crm_admin },
    { key: 'roles',        label: 'Roles',              icon: '🔑', group: 'access',   access: access?.settings_admin || access?.settings_view || access?.users_view || access?.hr_admin },
    { key: 'users_mgmt',  label: t('Users') as string, icon: '👤', group: 'access',   access: access?.users_view === true || access?.users_view_all === true || access?.users_edit === true || access?.hr_admin === true },
  ].filter(s => s.access === true || s.access === 1);

  const calculateOffset = (_tz: string) => {
    const offset = -new Date().getTimezoneOffset() / 60;
    return `${offset >= 0 ? '+' : ''}${offset - 1}`;
  };

  useEffect(() => {
    setUtcOffset(calculateOffset(timeZone || ''));
  }, [timeZone]);

  // Auto-select section: use defaultSection if valid, otherwise first available
  useEffect(() => {
    if (sections.length === 0) return;
    if (defaultSection && sections.find(s => s.key === defaultSection)) {
      setActiveSection(defaultSection);
    } else if (!sections.find(s => s.key === activeSection)) {
      setActiveSection(sections[0].key);
    }
  }, [sections.length, defaultSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    try {
      const [
        { data },
        { data: country },
        { data: currency },
        { data: language },
        { data: timezone },
        { data: date },
        { data: number },
      ] = await Promise.all([
        axios.get('user'),
        axios.get('static-country'),
        axios.get('static-currency'),
        axios.get('static-language'),
        axios.get('static-timezone'),
        axios.get('static-date'),
        axios.get('static-number'),
      ]);

      if (data) {
        setCompanyID(data.company.id);
        setCompanyName(data.company.title);
        // Company entity exposes these as camelCase properties (DB columns are static_*Id)
        setStaticCountrySelect(data.company.staticCountryId);
        setStaticCurrencySelect(data.company.staticCurrencyId);
        setStaticLanguageSelect(data.company.staticLanguageId);
        setStaticTimeZoneSelected(data.company.staticTimezoneId);
        setStaticDateSelect(data.company.staticDateId);
        setStaticNumberSelect(data.company.staticNumberId);
        setMailFrom(data.company.mail_from || '');
        setMailName(data.company.mail_name || '');
        setMailHost(data.company.mail_host || '');
        setMailAuth(data.company.mail_authMethod || '');
        setMailPort(data.company.mail_port || '');
        setFinanceMail(data.company.finance_email || '');
        setMailService(data.company.mail_service || '');
        setMailSll(Boolean(data.company.mail_secure));
        setMailUser(data.company.mail_user || '');
        setMailCronWeek(Boolean(data.company.mail_cron_week));
        setMailCronWeekApprove(Boolean(data.company.mail_cron_week_supervisor));
        if (data.company.mail_cron_week) {
          const parts = data.company.mail_cron_week.trim().split(' ');
          setTimeTo(parts[1]);
          setDayWeek(parts[4]);
          setTimeFrom(moment().hour(Number(parts[1])).minute(0).toDate());
        }
        if (data.company.mail_cron_week_supervisor) {
          const parts = data.company.mail_cron_week_supervisor.trim().split(' ');
          setTimeToApprove(parts[1]);
          setDayWeekApprove(parts[4]);
          setTimeFromApprove(moment().hour(Number(parts[1])).minute(0).toDate());
        }
        if (data.company.logoUrl) {
          setUrlImage(`${baseUrl}${data.company.logoUrl.replace(/^\//, '')}`);
        }
      }
      setStaticCountry(country);
      setStaticCurrency(currency);
      setStaticLanguage(language);
      setStaticTimeZone(timezone);
      setStaticDate(date);
      setStaticNumber(number);
      setUserTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    } catch {
      // auth errors handled by global interceptor
    }

    // Load history and users for Logs section
    try {
      const [usersRes, historyRes] = await Promise.allSettled([
        axios.get('users'),
        axios.post('history'),
      ]);
      if (usersRes.status === 'fulfilled') setUsers(Array.isArray(usersRes.value.data) ? usersRes.value.data : []);
      if (historyRes.status === 'fulfilled') setHistory(Array.isArray(historyRes.value.data) ? historyRes.value.data : []);
    } catch {}
  }, [baseUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTimeZoneChange = async (event: any) => {
    const selectedTimeZone = event.target.value;
    setStaticTimeZoneSelected(selectedTimeZone);
    const selectedZone = staticTimeZone.find((z: any) => z.id === Number(selectedTimeZone));
    setTimeZone(selectedZone?.name || '');
    try {
      await axios.put(`/company/${companyID}/`, {
        zone_description: selectedZone?.name || '',
        staticTimezoneId: selectedTimeZone == null ? null : Number(selectedTimeZone),
      });
      enqueueSnackbar('Time Zone change success', { variant: 'success' });
    } catch {
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const handleChangeSelected = async (event: any, keyName: any, set: any) => {
    set(event);
    try {
      // react-select returns { value, label }; extract .value if present
      const raw = event != null && typeof event === 'object' && 'value' in event ? event.value : event;
      await axios.put('company/general-settings', {
        [keyName]: keyName === 'unit' ? raw : (raw == null ? null : Number(raw)),
      });
      enqueueSnackbar('Change success', { variant: 'success' });
    } catch {
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const handleSubmit = async () => {
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar('Error: File must be PNG, JPG or JPEG', { variant: 'error' });
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      enqueueSnackbar('Error: File size must be less than 1MB', { variant: 'error' });
      return;
    }
    try {
      const formData = new FormData();
      formData.append('logo', file);
      await axios.post('company/upload/companyLogo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { data } = await axios.get('user');
      if (data) {
        setUrlImage(`${baseUrl}${(data.company.logoUrl || '').replace(/^\//, '')}`);
        setFile(null);
      }
      refreshUserHeder();
      enqueueSnackbar('Uploading success', { variant: 'success' });
    } catch {
      enqueueSnackbar('Error uploading file', { variant: 'error' });
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete('company/delete-logo');
    } catch {}
    setUrlImage('');
    setFile(null);
    refreshUserHeder();
  };

  const handleSaveMail = async () => {
    setIsLoading(true);
    try {
      const dataToSend: any = {
        mail_from: mailFrom,
        mail_name: mailName,
        mail_host: mailHost,
        mail_authMethod: mailAuth,
        mail_port: Number(mailPort),
        mail_secure: mailSll,
        mail_service: mailService,
        mail_user: mailUser,
        finance_email: financeMail,
      };
      if (mailPassword !== '*****') {
        dataToSend.mail_pass = mailPassword;
      }
      if (mailCronWeek) {
        dataToSend.mail_cron_time = timeTo;
        dataToSend.mail_cron_day = dayWeek;
      } else {
        dataToSend.mail_cron_week = null;
      }
      if (mailCronWeekApprove) {
        dataToSend.mail_cronToApprove_time = timeToApprove;
        dataToSend.mail_cronToApprove_day = dayWeekApprove;
      } else {
        dataToSend.mail_cron_week_supervisor = null;
      }
      await axios.put(`company/${Number(companyID)}/`, dataToSend);
      enqueueSnackbar('Mail change success', { variant: 'success' });
    } catch {
      enqueueSnackbar('Error', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckMail = async () => {
    setIsLoadingMail(true);
    try {
      await axios.get('company/mail/test');
      enqueueSnackbar('Email settings are working correctly.', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(`Error ${error.response?.data?.message || 'Unknown error'}`, {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } finally {
      setIsLoadingMail(false);
    }
  };

  const handleTimeFromChange = (time: Date) => {
    setTimeFrom(time);
    setTimeTo(time.getHours().toString());
  };

  const handleTimeFromChangeApprove = (time: Date) => {
    setTimeFromApprove(time);
    setTimeToApprove(time.getHours().toString());
  };


  const renderContent = () => {
    switch (activeSection) {
      case 'company':
        return (
          <>
            <Main
              urlImage={urlImage}
              handleDeleteLogo={handleDeleteLogo}
              file={file}
              handleSubmit={handleSubmit}
              setFile={setFile}
              companyName={companyName}
              handleTaskOrTasks={handleTaskOrTasks}
              taskOrTasks={taskOrTasks}
              handleTimeFormatChange={handleTimeFormatChange}
              is24HourFormat={is24HourFormat}
              access={access}
            />
            <SettingsBlock title="TOIL Recalculation" defaultOpen={false}>
              <ToilRecountPanel />
            </SettingsBlock>
          </>
        );
      case 'other':
        return (
          <Other
            staticCountrySelect={staticCountrySelect}
            handleChangeSelected={handleChangeSelected}
            setStaticCountrySelect={setStaticCountrySelect}
            staticCountry={staticCountry}
            staticCurrencySelect={staticCurrencySelect}
            setStaticCurrencySelect={setStaticCurrencySelect}
            staticCurrency={staticCurrency}
            staticDateSelect={staticDateSelect}
            setStaticDateSelect={setStaticDateSelect}
            staticDate={staticDate}
            staticNumberSelect={staticNumberSelect}
            setStaticNumberSelect={setStaticNumberSelect}
            staticNumber={staticNumber}
            staticLanguageSelect={staticLanguageSelect}
            setStaticLanguageSelect={setStaticLanguageSelect}
            staticLanguage={staticLanguage}
            staticTimeZoneSelected={staticTimeZoneSelected}
            handleTimeZoneChange={handleTimeZoneChange}
            staticTimeZone={staticTimeZone}
            userTimeZone={userTimeZone}
            utcOffset={utcOffset}
            unit={unit}
            setUnit={setUnit}
          />
        );
      case 'email':
        return (
          <>
            <TabMenu
              tabs={[
                { label: t('General') || 'General', value: 'general' },
                { label: t('Queue') || 'Queue',     value: 'queue'   },
                { label: t('History') || 'History', value: 'history' },
              ]}
              value={emailTab}
              onChange={setEmailTab}
            />
            {emailTab === 'general' && (
              <SettingsBlock title={t('General') || 'General'}>
                <div style={{ padding: '4px 16px 16px' }}>
                  <Mail
                    mailHost={mailHost}
                    setMailHost={setMailHost}
                    mailPort={mailPort}
                    setMailPort={setMailPort}
                    setMailSll={setMailSll}
                    mailSll={mailSll}
                    mailUser={mailUser}
                    setMailUser={setMailUser}
                    showPassword={showPassword}
                    mailPassword={mailPassword}
                    setMailPassword={setMailPassword}
                    setShowPassword={setShowPassword}
                    mailAuth={mailAuth}
                    setMailAuth={setMailAuth}
                    mailFrom={mailFrom}
                    setMailFrom={setMailFrom}
                    mailName={mailName}
                    setMailName={setMailName}
                    handleSaveMail={handleSaveMail}
                    mailService={mailService}
                    setMailService={setMailService}
                    isLoading={isLoading}
                    handleCheckMail={handleCheckMail}
                    isLoadingMail={isLoadingMail}
                  />
                </div>
              </SettingsBlock>
            )}
            {emailTab === 'queue'   && <MailQueuePanel view="queue" />}
            {emailTab === 'history' && <MailQueuePanel view="history" />}
          </>
        );
      case 'notifications':
        return (
          <Notifications
            financeMail={financeMail}
            setFinanceMail={setFinanceMail}
            mailCronWeek={mailCronWeek}
            setMailCronWeek={setMailCronWeek}
            dayWeek={dayWeek}
            setDayWeek={setDayWeek}
            timeFrom={timeFrom}
            handleTimeFromChange={handleTimeFromChange}
            mailCronWeekApprove={mailCronWeekApprove}
            setMailCronWeekApprove={setMailCronWeekApprove}
            dayWeekApprove={dayWeekApprove}
            setDayWeekApprove={setDayWeekApprove}
            timeFromApprove={timeFromApprove}
            handleTimeFromChangeApprove={handleTimeFromChangeApprove}
            is24HourFormat={is24HourFormat}
            handleSaveMail={handleSaveMail}
            isLoading={isLoading}
          />
        );
      case 'timesheets':
        return <TimesheetsSettings />;
      case 'hr':
        return <HrSettingsDrawer />;
      case 'payment':
        return (
          <SettingsBlock title={t('Billing') || 'Billing'}>
            <Payment companyID={companyID ?? undefined} />
          </SettingsBlock>
        );
      case 'logs':
        return (
          <TabMenu
            value={logsTab}
            onChange={setLogsTab}
            tabs={[
              { label: t('AllLogs') || 'All logs', value: 'all' },
              { label: t('RecalcLogTab') || 'TOIL & leave recalculation', value: 'recalc' },
            ]}
          >
            {logsTab === 'all' && <Logs history={history} users={users} />}
            {logsTab === 'recalc' && <RecalcLog />}
          </TabMenu>
        );
      case 'equipment':
        return <EquipmentSettings />;
      case 'finance':
        return <ExpensesSettings access={access} baseUrl={baseUrl} unit={unit} refreshFields={() => {}} />;
      case 'fields':
        return <FieldsSettings />;
      case 'crm':
        return <CrmSettings />;
      case 'disk':
        return <DiskPage access={access} baseUrl={baseUrl} embedded />;
      case 'roles':
        return <RoleMasterDetail access={access} refreshUserHeder={refreshUserHeder} />;
      case 'users_mgmt':
        return <UsersManagement access={access} />;
      default:
        return null;
    }
  };

  const GROUP_LABELS: Record<string, string> = {
    general:  'General',
    sections: 'Sections',
    tools:    t('Tools') || 'Tools',
    access:   'Access',
  };
  const navGroups = ['general', 'sections', 'tools', 'access'];

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left nav */}
      <div style={{ width: 196, flexShrink: 0, borderRight: '1px solid var(--border2-new)', overflowY: 'auto', padding: 10, display: hideSidebar ? 'none' : 'flex', flexDirection: 'column', gap: 0 }}>
        {navGroups.map(group => {
          const groupSections = sections.filter(s => s.group === group);
          if (groupSections.length === 0) return null;
          return (
            <div key={group}>
              <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--text-color-calendar-week)', padding: '10px 4px 6px' }}>
                {GROUP_LABELS[group]}
              </div>
              {groupSections.map(s => {
                const isActive = activeSection === s.key;
                const isSubItem = s.group === 'hr';
                return (
                  <div
                    key={s.key}
                    onClick={() => setActiveSection(s.key)}
                    style={{
                      display: 'flex', alignItems: 'center',
                      gap: isSubItem ? 6 : 8,
                      padding: isSubItem ? '5px 10px 5px 18px' : '7px 10px',
                      borderRadius: 8, cursor: 'pointer', marginBottom: 1,
                      background: isActive ? 'var(--active-nav)' : 'transparent',
                      border: 'none',
                      transition: 'background .15s',
                    }}
                    onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'var(--active-nav, rgba(37,99,235,.05))'; }}
                    onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <span style={{ fontSize: isSubItem ? 12 : 14, flexShrink: 0, opacity: isActive ? 1 : 0.6 }}>{s.icon}</span>
                    <span style={{
                      flex: 1,
                      fontSize: isSubItem ? 11 : 12,
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? 'var(--text-new)' : 'var(--text-color-calendar)',
                    }}>{s.label}</span>
                    {isActive && <span style={{ width: 3, height: 12, borderRadius: 2, background: 'rgba(37,99,235,.6)', flexShrink: 0 }} />}
                  </div>
                );
              })}
              {group !== navGroups[navGroups.length - 1] && (
                <div style={{ height: 1, background: 'var(--border2-new)', margin: '6px 0' }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Right content */}
      <SettingsPanelBody fill={activeSection === 'fields' || activeSection === 'logs'}>{renderContent()}</SettingsPanelBody>
    </div>
  );
};

export default SettingsTab;
